/* Copyright (c) Trimble Inc. All rights reserved. */

import { useEffect, useState, useCallback, useRef } from 'react';
import { AuthCheckHelper } from '../../helpers/AuthChecker.helper';
import { AuthCheckerModel, AuthCheckerProps } from './AuthChecker.model';
import { Logger } from '@tcp-base/toolkit';

/**
 * Hook for using AuthChecker functionality (Simplified)
 */
export const useAuthCheckController = ({
  onTokenValid,
  onTokenInvalid,
}: AuthCheckerProps): AuthCheckerModel => {
  const [isChecking, setIsChecking] = useState(true);
  const hasProcessedRef = useRef(false); // Prevent multiple executions

  /**
   * Check authentication state using service and handle results
   */
  const checkToken = useCallback(async () => {
    try {
      setIsChecking(true);
      hasProcessedRef.current = true; // Mark as processing

      // Use pure async function from AuthChecker service
      const checkResult = await AuthCheckHelper.checkAuthenticationState();

      // Handle different outcomes based on service result
      if (checkResult.shouldSkip) {
        // OAuth callback - skip processing
        setIsChecking(false);
        return;
      }

      if (checkResult.tokenValid && checkResult.token) {
        // Token is valid
        setIsChecking(false);
        onTokenValid?.(checkResult.token);
        return;
      }

      // Token is invalid or error occurred
      setIsChecking(false);
      onTokenInvalid?.();

      // Redirect if needed and possible
      if (checkResult.shouldRedirect) {
        AuthCheckHelper.performLoginRedirect().catch((error) => {
          Logger.error('AuthChecker: Login redirect failed:', error);
          hasProcessedRef.current = false; // Reset on failure to allow retry
        });
      } else Logger.error('AuthChecker:', checkResult.error);
      hasProcessedRef.current = false; // Reset on error
    } catch (error) {
      Logger.error('AuthChecker: Unexpected error:', error);
      setIsChecking(false);
      onTokenInvalid?.();
      hasProcessedRef.current = false; // Reset on error to allow retry
    }
  }, []); // Remove dependencies to make callback stable

  /**
   * ize token check on mount - only runs once now
   */
  useEffect(() => {
    checkToken();
    // Cleanup function to reset ref if component unmounts
    return (): void => {
      hasProcessedRef.current = false;
    };
  }, []); // Empty dependency array - runs only once

  return {
    isChecking,
  };
};

export default useAuthCheckController;
